import { useCallback, useEffect } from 'react';

import { Background } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { Skeleton, TableSkeleton } from '@Components/Skeleton/Skeleton';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { WidgetContainer } from '@Components/WidgetContainer';

import { useSetError } from '@src/redux/modals';

import { DagGraphRow } from './dag-graphs-constants';
import { useDagGraphs } from './use-dag-graphs';

import './DagGraphs.scss';

const HEADERS: TypedHeader<DagGraphRow>[] = [
  {
    sortPriority: 1,
    sortAscending: true,
    flex: 2,
    label: 'Name',
    name: 'name',
    renderer: data => data.name
  },
  {
    flex: 2,
    label: 'Cron',
    name: 'cron',
    renderer: data => data.cron
  },
  {
    flex: 2,
    label: 'Time Zone',
    name: 'tz',
    renderer: data => data.tz
  }
];

export const DagGraphs = () => {
  const setError = useSetError();
  const { data, isLoading, error } = useDagGraphs();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load data",
        message: `Failed to load dag graphs: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // BPMTable has no loading concept of its own, so this fills its noRowsRenderer: a skeleton
  // while fetching, otherwise the truly-empty fallback. Keeping BPMTable always mounted (rather
  // than swapping the whole widget for a spinner) keeps the search/filter bar visible and
  // interactive throughout.
  const renderTableLoading = useCallback(() => {
    if (isLoading) {
      return (
        <Skeleton>
          <TableSkeleton rowHeight={45} headerHeight={40} yOffset={8} />
        </Skeleton>
      );
    }
    return <TableEmptyState />;
  }, [isLoading]);

  return (
    <Page title='Dag Graphs' pageType='Dag Graphs' isLoading={isLoading}>
      <div className='bpm-dag-graphs'>
        <WidgetContainer className='dag-graphs-widget' background={Background.DARK} fillHeight>
          <div className='dag-graphs-table' data-testid='dag-graphs-table'>
            <BPMTable
              headers={HEADERS}
              data={data ?? []}
              noRowsRenderer={renderTableLoading}
              rowHeight={45}
              headerHeight={40}
              defaultTokens={{
                advanced: ['Name', 'is like', 'mobius']
              }}
              defaultAdvancedFilter
            />
          </div>
        </WidgetContainer>
      </div>
    </Page>
  );
};
